/*
  The Card Info panel, styled as the portal's CardInformation: a tab strip over compact
  label/value rows. The public page has one section, so the strip carries a single active
  tab as a heading — the look of the portal without the Certification/Details/Logs tabs,
  which hold office-only fields (D4).

  The rows come from selectCardInfoRows only; game, set and card name link into the pop
  report as they did on the old table.
*/

import { ReactNode } from 'react';

import CardLink from 'components/CardLink';
import GameLink from 'components/GameLink';
import SetLink from 'components/SetLink';
import { InfoRow, ReportRow } from 'lib/gradeReport';

function CardInfoPanel({ rows, row, headingId }: { rows: InfoRow[]; row: ReportRow; headingId: string }) {
  const valueFor = (entry: InfoRow): ReactNode => {
    switch (entry.key) {
      case 'game':
        return <GameLink cardInfo={row.cardInfo} game={entry.value} />;
      case 'set':
        return <SetLink cardInfo={row.cardInfo} setName={entry.value} />;
      case 'card':
        return <CardLink cardInfo={row.cardInfo} cardName={entry.value} />;
      default:
        return entry.value;
    }
  };

  return (
    <section aria-labelledby={headingId} className="skin-surface overflow-hidden rounded-md border border-pcg-border bg-pcg-panel">
      <div className="border-b border-pcg-goldDark/20 bg-pcg-surfaceAlt">
        <h2
          id={headingId}
          className="m-0 inline-block border-b-2 border-pcg-gold px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-pcg-goldText"
        >
          Card Info
        </h2>
      </div>
      <dl className="m-0">
        {rows.map((entry) => (
          <div
            key={entry.key}
            className="flex gap-3 border-b border-pcg-borderSoft px-3 py-1 last:border-b-0"
          >
            <dt className="w-[40%] shrink-0 text-xs font-medium uppercase tracking-wide text-pcg-textMuted">
              {entry.label}
            </dt>
            <dd className="m-0 min-w-0 break-words text-[13px] font-medium text-pcg-text">
              {valueFor(entry)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default CardInfoPanel;
